import React from "react";
import {
  X,
  Mail,
  Phone,
  Calendar,
  Clock,
  UserRound,
  CreditCard,
} from "lucide-react";
import { motion } from "framer-motion";
import { Booking } from "../services/BookingsApi";
import { useAuth } from "../auth/AuthContext";

interface BookingDetailsDrawerProps {
  isOpen: boolean;
  booking: Booking | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onDelete: (bookingId: number) => void;
  onEdit?: (bookingId: number) => void;
}

const InfoRow = ({
  label,
  value,
  icon,
}: {
  label: string;
  value?: React.ReactNode;
  icon?: React.ReactNode;
}) => (
  <div className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-b-0">
    <div className="flex items-center gap-2 text-sm text-gray-500 min-w-[140px]">
      {icon}
      <span className="font-medium text-gray-600">{label}</span>
    </div>
    <div className="text-sm font-semibold text-gray-900 flex-1">{value || "—"}</div>
  </div>
);

const BookingDetailsDrawer: React.FC<BookingDetailsDrawerProps> = ({
  isOpen,
  booking,
  loading,
  error,
  onClose,
  onDelete,
  onEdit,
}) => {
  const { user } = useAuth();
  const authCurrency = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  const currency =
    booking?.rawData?.company?.currency?.currency_symbol ||
    booking?.rawData?.company?.currency?.currency_code ||
    authCurrency;

  const items = Array.isArray(booking?.rawData?.items)
    ? booking?.rawData?.items || []
    : [];

  const payments =
    (Array.isArray((booking?.rawData as any)?.payments)
      ? (booking?.rawData as any).payments
      : []) || [];

  // Calculate totals from items array - sum all items (services + products)
  const calculatedServiceTotal = items.reduce((sum, item: any) => {
    const quantity = item?.qty || item?.quantity || 1;
    const unitPrice = Number(item?.unit_price || 0);
    // Use the amount field if available, otherwise calculate from unit_price * quantity
    const amount = Number(item?.amount) || (unitPrice * quantity);
    return sum + amount;
  }, 0);

  const bookingDate = booking?.rawData?.date_time
    ? new Date(booking.rawData.date_time).toLocaleDateString()
    : booking?.bookingTime?.split(" ")?.[0];

  const bookingTime = booking?.rawData?.date_time
    ? new Date(booking.rawData.date_time).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    })
    : booking?.bookingTime?.split(" ").slice(1).join(" ");

  const handleDeleteClick = () => {
    if (booking) {
      onDelete(booking.id);
    }
  };

  const handleEditClick = () => {
    if (booking && onEdit) {
      onEdit(booking.id);
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[99]" onClick={onClose} />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-2xl z-[100] transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        {isOpen && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Close booking details"
            className="fixed lg:-left-12 top-4 lg:top-9 right-4 lg:right-auto w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center text-[#885ABB] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
            onClick={onClose}
          >
            <X strokeWidth={3} size={20} className="group-hover:rotate-90 transition-transform duration-300" />
          </motion.button>
        )}

        <div className="h-full flex flex-col">
          <div className="border-b border-gray-100 px-8 py-6">
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
              Booking Details
            </p>
            <h2 className="text-2xl font-semibold text-[#402B69]">
              {booking?.customerName || "Loading..."}
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {loading && (
              <div className="flex h-full items-center justify-center text-gray-500">
                Loading booking...
              </div>
            )}

            {!loading && booking && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-[#FCFCFD] border border-gray-100 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-14 h-14 rounded-full bg-purple-100 text-[#885ABB] flex items-center justify-center">
                        <UserRound className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
                          Customer
                        </p>
                        <h3 className="text-lg font-semibold text-[#402B69]">
                          {booking.customerName}
                        </h3>
                      </div>
                    </div>

                    <div className="divide-y divide-gray-100">
                      <InfoRow
                        label="Email"
                        value={booking.rawData?.customer?.email}
                        icon={<Mail className="w-4 h-4 text-[#885ABB]" />}
                      />
                      <InfoRow
                        label="Phone"
                        value={
                          ((booking.rawData?.customer as any)?.phone ||
                            (booking.rawData?.customer as any)?.phone_number ||
                            (booking.rawData?.customer as any)?.mobile ||
                            "N/A") as React.ReactNode
                        }
                        icon={<Phone className="w-4 h-4 text-[#885ABB]" />}
                      />
                      <InfoRow
                        label="Booking Date"
                        value={bookingDate || "—"}
                        icon={<Calendar className="w-4 h-4 text-[#885ABB]" />}
                      />
                      <InfoRow
                        label="Booking Time"
                        value={bookingTime || "—"}
                        icon={<Clock className="w-4 h-4 text-[#885ABB]" />}
                      />
                      <InfoRow
                        label="Booking Status"
                        value={
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                            {booking.status}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Payment Status"
                        value={
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${booking.pending === 0
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                              }`}
                          >
                            {booking.pending === 0 ? "Paid" : "Pending"}
                          </span>
                        }
                      />
                    </div>
                  </div>

                  <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-[#402B69]">
                        Payment Summary
                      </h3>
                      <CreditCard className="w-5 h-5 text-[#885ABB]" />
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Service Total</span>
                        <span className="font-semibold text-gray-900">
                          {currency}
                          {calculatedServiceTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Tax</span>
                        <span className="font-semibold text-gray-900">
                          {booking.rawData?.tax_amount || booking.rawData?.total_tax
                            ? `${currency}${Number(booking.rawData?.tax_amount || booking.rawData?.total_tax || 0).toFixed(2)}`
                            : `${currency}0.00`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Paid</span>
                        <span className="font-semibold text-gray-900">
                          {currency}
                          {booking.paid.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-base border-t border-gray-100 pt-3">
                        <span className="font-semibold text-[#402B69]">
                          Remaining
                        </span>
                        <span className="font-bold text-[#885ABB]">
                          {currency}
                          {booking.pending.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-[#402B69]">
                        Services
                      </h3>
                    </div>
                  </div>
                  {items.length === 0 ? (
                    <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-xl">
                      No line items attached to this booking.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-[#f6f0fb] text-[#402B69]">
                          <tr>
                            <th className="text-left px-4 py-2 font-semibold">#</th>
                            <th className="text-left px-4 py-2 font-semibold">
                              Service
                            </th>
                            <th className="text-left px-4 py-2 font-semibold">
                              Type
                            </th>
                            <th className="text-right px-4 py-2 font-semibold">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, idx) => {
                            const quantity =
                              (item as any)?.qty ||
                              (item as any)?.quantity ||
                              1;
                            const unitPrice = Number((item as any)?.unit_price || 0);
                            const amount =
                              Number((item as any)?.amount) || unitPrice * quantity;

                            return (
                              <tr
                                key={(item as any)?.id || idx}
                                className="border-b border-gray-100 last:border-b-0"
                              >
                                <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                                <td className="px-4 py-3 text-gray-900 font-medium">
                                  {(item as any)?.name ||
                                    (item as any)?.business_service?.name ||
                                    (item as any)?.product?.name ||
                                    "Line item"}
                                </td>
                                <td className="px-4 py-3 text-gray-500 capitalize">
                                  {(item as any)?.type || "service"}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-900">
                                  {currency}
                                  {amount.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingDetailsDrawer;
